import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, matchRoutes, useLocation } from 'react-router-dom';

import FuseUtils from '@fuse/utils';
import AppContext from 'app/AppContext';
import settingsConfig from 'app/fuse-configs/settingsConfig';
import useConfigureNavbar from 'app/hooks/useConfigureNavbar';

const LOGIN_URL = '/login';
const DEFAULT_LOGIN_REDIRECT_URL = settingsConfig.loginRedirectUrl || '/';

const pathOf = ({ pathname, search, hash }) => `${pathname}${search}${hash}`;

/**
 * Gates the whole route tree on the current user's roles.
 *
 * A guest is sent to the login page carrying the page they asked for in the router's
 * location state, so once they have roles they land back on it, query string and all.
 */
const FuseAuthorization = React.memo(({ children }) => {
  const location = useLocation();
  const { routes } = useContext(AppContext);
  const userRoles = useSelector((state) => state.auth.user.roles);

  useConfigureNavbar();

  const matched = matchRoutes(routes, location.pathname)?.[0];
  const accessGranted = matched ? FuseUtils.hasPermission(matched.route.auth, userRoles) : true;

  if (accessGranted) return <>{children}</>;

  const isGuest = !userRoles || userRoles.length === 0;
  if (isGuest) {
    // A path rather than the Location itself: a Location carries its own `state.from`, so a
    // user bounced to a page they still cannot see would be sent back and forth forever.
    const from = location.pathname === LOGIN_URL ? undefined : pathOf(location);
    return <Navigate to={LOGIN_URL} state={{ from }} replace />;
  }

  // Either the user has just logged in, or they reached a page their roles don't cover.
  return <Navigate to={location.state?.from || DEFAULT_LOGIN_REDIRECT_URL} replace />;
});

export default FuseAuthorization;
